/**
 * Root manager for the Babylon.js system
 * Coordinates all sub-managers and provides the main API
 */

import type { Scene } from "@babylonjs/core";
import { getLogger } from "../utils/log";
import { ConfigurationManager } from "./config/ConfigurationManager";
import { BaseManager } from "./core/BaseManager";
import { EventBus } from "./core/EventBus";
import type { BabylonConfig } from "./core/types";
import { EngineManager } from "./engine/EngineManager";
import type { AnyEngine, EngineOptions } from "./engine/types";
import { SceneManager } from "./scene/SceneManager";
import type { SceneOptions } from "./scene/types";

/**
 * Main manager that coordinates all Babylon.js functionality
 * Implements singleton pattern for global access
 */
const log = getLogger("manager");

export class BabylonManager extends BaseManager {
  private static instance: BabylonManager | null = null;
  private config: BabylonConfig = {};
  private configurationManager: ConfigurationManager | null = null;
  private engineManager: EngineManager | null = null;
  private sceneManager: SceneManager | null = null;

  /**
   * Private constructor for singleton pattern
   */
  private constructor() {
    super(new EventBus());
  }

  /**
   * Get the singleton instance of BabylonManager
   * @param forceSingleton If false, creates a new instance instead of using singleton
   * @returns The BabylonManager instance
   */
  static getInstance(forceSingleton = true): BabylonManager {
    if (!forceSingleton) {
      return new BabylonManager();
    }
    // Create new instance if singleton is null or was disposed
    // This handles race conditions during tab navigation where a provider
    // may dispose the singleton while another provider still holds a reference
    if (!BabylonManager.instance || BabylonManager.instance.isDisposed) {
      BabylonManager.instance = new BabylonManager();
    }
    return BabylonManager.instance;
  }

  /**
   * Reset the singleton instance (mainly for testing)
   */
  static resetInstance(): void {
    if (BabylonManager.instance) {
      BabylonManager.instance.dispose();
      BabylonManager.instance = null;
    }
  }

  /**
   * Initialize the Babylon system with configuration
   * @param config The configuration for the system
   */
  async initialize(config: BabylonConfig = {}): Promise<void> {
    // Initialize configuration manager first
    this.configurationManager = new ConfigurationManager(this.eventBus, config);
    await this.configurationManager.initialize();

    // Get validated configuration
    this.config = this.configurationManager.getConfig();

    await super.initialize();
  }

  /**
   * Implementation-specific initialization
   */
  protected async onInitialize(): Promise<void> {
    // ConfigurationManager already initialized in initialize()
    log("initializing with config %O", this.config);

    // Initialize engine manager
    this.engineManager = new EngineManager(this.eventBus, {
      debug: this.config.debug,
    });
    await this.engineManager.initialize();

    // Initialize scene manager
    this.sceneManager = new SceneManager(this.eventBus, {
      debug: this.config.debug,
    });
    await this.sceneManager.initialize();

    // Emit ready event
    this.emit("ready", {
      config: this.config,
      timestamp: Date.now(),
    });
  }

  /**
   * Override dispose to handle EventBus disposal
   */
  dispose(): void {
    if (this.isDisposed) return;

    // Call parent dispose (which emits events)
    super.dispose();

    // Now dispose the EventBus after events are emitted
    this.eventBus.dispose();
  }

  /**
   * Implementation-specific disposal
   */
  protected onDispose(): void {
    // Dispose of sub-managers in reverse order
    if (this.sceneManager) {
      this.sceneManager.dispose();
      this.sceneManager = null;
    }
    if (this.engineManager) {
      this.engineManager.dispose();
      this.engineManager = null;
    }
    if (this.configurationManager) {
      this.configurationManager.dispose();
      this.configurationManager = null;
    }

    // Clear the singleton instance
    BabylonManager.instance = null;
  }

  /**
   * Get the current configuration
   */
  getConfig(): Readonly<BabylonConfig> {
    return { ...this.config };
  }

  /**
   * Get the configuration manager
   */
  getConfigurationManager(): ConfigurationManager | null {
    return this.configurationManager;
  }

  /**
   * Update the configuration (requires re-initialization)
   * @param config The new configuration
   */
  async updateConfig(config: Partial<BabylonConfig>): Promise<void> {
    this.ensureNotDisposed();

    if (this.isInitialized) {
      throw new Error(
        "Cannot update config after initialization. Dispose and reinitialize instead.",
      );
    }

    this.config = {
      ...this.config,
      ...config,
    };

    this.emit("configUpdated", { config: this.config });
  }

  /**
   * Create an engine with the specified options
   * @param options The engine options
   * @returns The created engine
   */
  async createEngine(options: EngineOptions): Promise<AnyEngine> {
    this.ensureInitialized();

    if (!this.engineManager) {
      throw new Error("EngineManager not initialized");
    }

    return this.engineManager.createEngine(options);
  }

  /**
   * Get the engine instance
   * @returns The engine or null if not initialized
   */
  getEngine(): AnyEngine | null {
    return this.engineManager?.getEngine() ?? null;
  }

  /**
   * Create a scene with the specified options
   * @param sceneIdOrOptions Either a scene ID string, or scene options
   * @param options The scene options (if first param is scene ID)
   * @returns The created scene
   */
  async createScene(
    sceneIdOrOptions?: string | SceneOptions,
    options?: SceneOptions,
  ): Promise<Scene> {
    this.ensureInitialized();

    const engine = this.getEngine();
    if (!engine) {
      throw new Error(
        "Engine must be created before scene. Call createEngine first.",
      );
    }

    if (!this.sceneManager) {
      throw new Error("SceneManager not initialized");
    }

    // Handle overloaded parameters
    let sceneId: string | undefined;
    let sceneOptions: SceneOptions | undefined;

    if (typeof sceneIdOrOptions === "string") {
      sceneId = sceneIdOrOptions;
      sceneOptions = options;
    } else {
      sceneOptions = sceneIdOrOptions;
    }

    return this.sceneManager.createScene(engine, sceneId, sceneOptions);
  }

  /**
   * Get the scene instance
   * @param sceneId Optional scene ID to retrieve
   * @returns The scene or null if not initialized
   */
  getScene(sceneId?: string): Scene | null {
    return this.sceneManager?.getScene(sceneId) ?? null;
  }

  /**
   * Register an externally-created scene for devtools tracking.
   * Useful for scenes created outside manager flow (e.g., physics demos).
   * @param sceneId Unique identifier for the scene
   * @param scene The Babylon.js scene to register
   */
  registerScene(sceneId: string, scene: Scene): void {
    this.ensureInitialized();
    this.sceneManager?.registerScene(sceneId, scene);
  }

  /**
   * Unregister a scene without disposing it.
   * Use when external code manages scene lifecycle.
   * @param sceneId The scene ID to unregister
   */
  unregisterScene(sceneId: string): void {
    this.sceneManager?.unregisterScene(sceneId);
  }

  /**
   * Generate a unique scene ID for multi-instance scenarios
   * @returns A unique scene ID
   */
  generateSceneId(): string {
    return `scene_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get the engine manager
   * @returns The engine manager or null
   */
  getEngineManager(): EngineManager | null {
    return this.engineManager;
  }

  /**
   * Get the scene manager
   * @returns The scene manager or null
   */
  getSceneManager(): SceneManager | null {
    return this.sceneManager;
  }

  /**
   * Check if the system is ready for use
   */
  get isReady(): boolean {
    return this.isInitialized && !this.isDisposed;
  }

  /**
   * Wait for the manager to finish initialization
   */
  async waitForReady(): Promise<void> {
    if (this.isReady) {
      return;
    }

    if (this.isDisposed) {
      throw new Error("BabylonManager has been disposed");
    }

    if (!this.isInitialized && !this.isInitializing) {
      throw new Error(
        "BabylonManager is not initializing. Call initialize() before waiting for readiness.",
      );
    }

    await new Promise<void>((resolve, reject) => {
      const cleanup = () => {
        unsubscribeReady();
        unsubscribeError();
        unsubscribeDisposed();
      };

      const handleReady = () => {
        cleanup();
        resolve();
      };

      const handleError = (data: { error: Error }) => {
        cleanup();
        reject(
          data?.error ?? new Error("BabylonManager initialization failed"),
        );
      };

      const handleDisposed = () => {
        cleanup();
        reject(new Error("BabylonManager was disposed before it became ready"));
      };

      const unsubscribeReady = this.onceEvent("ready", handleReady);
      const unsubscribeError = this.onceEvent("error", handleError);
      const unsubscribeDisposed = this.onceEvent("disposed", handleDisposed);
    });
  }

  /**
   * Subscribe to system events
   * @param event The event name
   * @param handler The event handler
   * @returns Function to unsubscribe
   */
  onEvent<T = unknown>(event: string, handler: (data: T) => void): () => void {
    return this.on(event, handler);
  }

  /**
   * Subscribe to system events once
   * @param event The event name
   * @param handler The event handler
   * @returns Function to unsubscribe
   */
  onceEvent<T = unknown>(
    event: string,
    handler: (data: T) => void,
  ): () => void {
    return this.once(event, handler);
  }

  /**
   * Get the internal event bus (for advanced usage)
   * @returns The event bus instance
   */
  getEventBus(): EventBus {
    this.ensureNotDisposed();
    return this.eventBus;
  }
}
